import { AuthorAdminService } from './authorAdminService';
import { CategoryAdminService } from './categoryAdminService';
import { DocumentAdminService } from './documentAdminService';
import { FAQAdminService } from './faqAdminService';
import { ListItemAdminService } from './listItemAdminService';
import { PageAdminService } from './pageAdminService';
import { SectionAdminService } from './sectionAdminService';
import { TagAdminService } from './tagAdminService';
import { EntityName } from '@models/entity';
import { VideoAdminService } from './videoAdminService';
import { FooterNavigationAdminService } from './footerNavigationAdminService';
import { MenuNavigationAdminService } from './menuNavigationAdminService';
import { WhitePaperAdminService } from './whitePaperAdminService';
import { PressReleaseAdminService } from './pressReleaseAdminService';
import { SiteSettingAdminService } from './siteSettingService';
import { SuccessStoryAdminService } from './successStoryAdminService';
import { HrefLangManagerAdminService } from './HrefLangManagerAdminService';
import { BlogAdminService } from './blogAdminService';
import { GlossaryAdminService } from './glossaryAdminService';
import { ExternalResourceLinkAdminService } from './externalResourceLinkAdminService';

interface ServiceMapping {
    pageAdminService: PageAdminService;
    authorAdminService: AuthorAdminService;
    categoryAdminService: CategoryAdminService;
    tagAdminService: TagAdminService;
    documentAdminService: DocumentAdminService;
    listItemAdminService: ListItemAdminService;
    videoAdminService: VideoAdminService;
    faqAdminService: FAQAdminService;
    menuNavigationAdminService: MenuNavigationAdminService<1>;
    menuNavigationV2AdminService: MenuNavigationAdminService<2>;
    footerNavigationAdminService: FooterNavigationAdminService;
    whitePaperAdminService: WhitePaperAdminService;
    successStoryAdminService: SuccessStoryAdminService;
    pressReleaseAdminService: PressReleaseAdminService;
    blogAdminService: BlogAdminService;
    sectionAdminService: SectionAdminService;
    hrefLangManagerAdminService: HrefLangManagerAdminService;
    siteSettingAdminService: SiteSettingAdminService;
    glossaryAdminService: GlossaryAdminService;
    externalResourceLinkAdminService: ExternalResourceLinkAdminService;
}

export class AdminApiService {
    private static serviceInstances: Partial<ServiceMapping> = {};

    private static getService<T extends keyof ServiceMapping>(
        serviceClass: new () => ServiceMapping[T],
        serviceKey: T
    ): ServiceMapping[T] {
        return (
            AdminApiService.serviceInstances[serviceKey] ??
            (AdminApiService.serviceInstances[serviceKey] = new serviceClass())
        );
    }

    public static get pageAdminService(): PageAdminService {
        return AdminApiService.getService(PageAdminService, 'pageAdminService');
    }

    public static get authorAdminService(): AuthorAdminService {
        return AdminApiService.getService(AuthorAdminService, 'authorAdminService');
    }

    public static get categoryAdminService(): CategoryAdminService {
        return AdminApiService.getService(CategoryAdminService, 'categoryAdminService');
    }

    public static get tagAdminService(): TagAdminService {
        return AdminApiService.getService(TagAdminService, 'tagAdminService');
    }

    public static get documentAdminService(): DocumentAdminService {
        return AdminApiService.getService(DocumentAdminService, 'documentAdminService');
    }

    public static get sectionAdminService(): SectionAdminService {
        return AdminApiService.getService(SectionAdminService, 'sectionAdminService');
    }

    public static get videoAdminService(): VideoAdminService {
        return AdminApiService.getService(VideoAdminService, 'videoAdminService');
    }

    public static get listAdminService(): ListItemAdminService {
        return AdminApiService.getService(ListItemAdminService, 'listItemAdminService');
    }

    public static get faqAdminService(): FAQAdminService {
        return AdminApiService.getService(FAQAdminService, 'faqAdminService');
    }

    public static get menuNavigationAdminService(): MenuNavigationAdminService<1> {
        return AdminApiService.getService(MenuNavigationAdminService, 'menuNavigationAdminService');
    }

    public static get menuNavigationV2AdminService(): MenuNavigationAdminService<2> {
        return AdminApiService.getService(MenuNavigationAdminService, 'menuNavigationV2AdminService');
    }

    public static get footerNavigationAdminService(): FooterNavigationAdminService {
        return AdminApiService.getService(FooterNavigationAdminService, 'footerNavigationAdminService');
    }

    public static get whitePaperAdminService(): WhitePaperAdminService {
        return AdminApiService.getService(WhitePaperAdminService, 'whitePaperAdminService');
    }

    public static get successStoryAdminService(): SuccessStoryAdminService {
        return AdminApiService.getService(SuccessStoryAdminService, 'successStoryAdminService');
    }

    public static get pressReleaseAdminService(): PressReleaseAdminService {
        return AdminApiService.getService(PressReleaseAdminService, 'pressReleaseAdminService');
    }

    public static get hrefLangManagerAdminService(): HrefLangManagerAdminService {
        return AdminApiService.getService(HrefLangManagerAdminService, 'hrefLangManagerAdminService');
    }

    public static get blogAdminService(): BlogAdminService {
        return AdminApiService.getService(BlogAdminService, 'blogAdminService');
    }

    public static get siteSettingAdminService(): SiteSettingAdminService {
        return AdminApiService.getService(SiteSettingAdminService, 'siteSettingAdminService');
    }

    public static get glossaryAdminService(): GlossaryAdminService {
        return AdminApiService.getService(GlossaryAdminService, 'glossaryAdminService');
    }

    public static get externalResourceLinkAdminService(): ExternalResourceLinkAdminService {
        return AdminApiService.getService(ExternalResourceLinkAdminService, 'externalResourceLinkAdminService');
    }
}

type adminServiceInstanceType =
    | null
    | AuthorAdminService
    | CategoryAdminService
    | TagAdminService
    | PageAdminService
    | DocumentAdminService
    | VideoAdminService
    | WhitePaperAdminService
    | SuccessStoryAdminService
    | PressReleaseAdminService
    | BlogAdminService
    | GlossaryAdminService;

export function getApiServiceInstance(entityName: EntityName.Author): AuthorAdminService;
export function getApiServiceInstance(entityName: EntityName.Category): CategoryAdminService;
export function getApiServiceInstance(entityName: EntityName.Tag): TagAdminService;
export function getApiServiceInstance(entityName: EntityName.Page): PageAdminService;
export function getApiServiceInstance(entityName: EntityName.Document): DocumentAdminService;
export function getApiServiceInstance(entityName: EntityName.Video): VideoAdminService;
export function getApiServiceInstance(entityName: EntityName.WhitePaperArticle): WhitePaperAdminService;
export function getApiServiceInstance(entityName: EntityName.SuccessStoryArticle): SuccessStoryAdminService;
export function getApiServiceInstance(entityName: EntityName.PressReleaseArticle): PressReleaseAdminService;
export function getApiServiceInstance(entityName: EntityName.BlogArticle): BlogAdminService;
export function getApiServiceInstance(entityName: EntityName.GlossaryArticle): GlossaryAdminService;

export function getApiServiceInstance(entityName: string): adminServiceInstanceType;

export function getApiServiceInstance(entityName: string): adminServiceInstanceType {
    switch (entityName) {
        case EntityName.Author:
            return AdminApiService.authorAdminService;
        case EntityName.Category:
            return AdminApiService.categoryAdminService;
        case EntityName.Tag:
            return AdminApiService.tagAdminService;
        case EntityName.Page:
            return AdminApiService.pageAdminService;
        case EntityName.Document:
            return AdminApiService.documentAdminService;
        case EntityName.Video:
            return AdminApiService.videoAdminService;
        case EntityName.WhitePaperArticle:
            return AdminApiService.whitePaperAdminService;
        case EntityName.SuccessStoryArticle:
            return AdminApiService.successStoryAdminService;
        case EntityName.PressReleaseArticle:
            return AdminApiService.pressReleaseAdminService;
        case EntityName.BlogArticle:
            return AdminApiService.blogAdminService;
        case EntityName.GlossaryArticle:
            return AdminApiService.glossaryAdminService;
        default:
            return null;
    }
}
